/**
 * Client-side OLS scoring for Joint Decisions regression mode.
 * Matches analyze_jointdec_strata.build_design_matrix (get_dummies drop_first).
 */

import { isFusionProcedureKey } from './jointdecEncounterNote';

/** Non-union is a fusion outcome. Decompression and motion-preserving surgery are N/A. */
export function nonUnionApplies(procedureKey) {
  return isFusionProcedureKey(procedureKey);
}

function clipPrediction(value, clip) {
  let v = Number(value);
  if (!Number.isFinite(v)) return null;
  const lo = clip?.[0];
  const hi = clip?.[1];
  if (lo != null && Number.isFinite(lo)) v = Math.max(lo, v);
  if (hi != null && Number.isFinite(hi)) v = Math.min(hi, v);
  return v;
}

/** Active dummy term names for one factor row (plus intercept). */
export function activeRegressionTerms(factors, categories) {
  const active = new Set(['intercept']);
  if (!categories || !factors) return active;
  Object.entries(categories).forEach(([predictor, levels]) => {
    if (!Array.isArray(levels) || levels.length === 0) return;
    const raw = factors[predictor];
    if (raw === undefined || raw === null || raw === '') return;
    const value = typeof levels[0] === 'number' ? Number(raw) : String(raw);
    const ref = levels[0];
    const refNorm = typeof ref === 'number' ? Number(ref) : String(ref);
    if (value === refNorm) return;
    // Coerce level match for numeric categories stored as strings in coef keys.
    const levelKey = levels.find((lvl) => {
      if (typeof lvl === 'number') return Number(value) === lvl;
      return String(value) === String(lvl);
    });
    if (levelKey === undefined || levelKey === ref) return;
    active.add(`${predictor}_${levelKey}`);
  });
  return active;
}

export function scoreRegressionOutcome(models, outcome, factors) {
  const spec = models?.outcomes?.[outcome];
  if (!spec?.coefs) return null;
  const procedureKey = factors?.procedure_key;
  // Fusion-only, even when a stale model still marks non_union applicable.
  if (outcome === 'non_union' && !nonUnionApplies(procedureKey)) return null;
  if (
    procedureKey &&
    models.applicable?.[procedureKey] &&
    Object.prototype.hasOwnProperty.call(models.applicable[procedureKey], outcome) &&
    models.applicable[procedureKey][outcome] === false
  ) {
    return null;
  }
  const active = activeRegressionTerms(factors, models.categories);
  let y = 0;
  Object.entries(spec.coefs).forEach(([term, beta]) => {
    if (term === 'intercept' || active.has(term)) {
      y += Number(beta) || 0;
    }
  });
  return clipPrediction(y, spec.clip);
}

/** Build a strata-shaped row of predicted metric fields. */
export function scoreRegressionStratumRow(models, factors) {
  if (!models?.outcomes) return null;
  const row = {
    sex: factors.sex,
    age_bucket: factors.age_bucket,
    bmi_bucket: factors.bmi_bucket,
    smoking: factors.smoking,
    diabetes: factors.diabetes,
    antithrombotic: factors.antithrombotic,
    workers_comp: factors.workers_comp,
    revision_surgery: factors.revision_surgery,
    levels: factors.levels,
    procedure_key: factors.procedure_key,
  };
  Object.keys(models.outcomes).forEach((outcome) => {
    row[outcome] = scoreRegressionOutcome(models, outcome, factors);
  });
  // Medical post-surgery rates must be at least the matching control rate.
  [
    ['death_surgery', 'death_control'],
    ['stroke_surgery', 'stroke_control'],
    ['mi_surgery', 'mi_control'],
    ['pe_surgery', 'pe_control'],
    ['dvt_surgery', 'dvt_control'],
  ].forEach(([surgeryKey, controlKey]) => {
    const surgery = Number(row[surgeryKey]);
    const control = Number(row[controlKey]);
    if (Number.isFinite(surgery) && Number.isFinite(control) && surgery < control) {
      row[surgeryKey] = control;
    }
  });
  return row;
}
